/**
 * Format a date for display
 * @param {Date|Object} date - Date object or Firestore Timestamp
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDate(date, options = {}) {
    if (!date) return '';

    // Handle Firestore Timestamps
    const dateObj = date.toDate ? date.toDate() : new Date(date);

    const defaultOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...options
    };

    return dateObj.toLocaleDateString('en-US', defaultOptions);
}

/**
 * Format a number as currency
 * @param {number} amount - Amount in dollars
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency
    }).format(amount);
}

/**
 * Truncate text to a maximum length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export function truncateText(text, maxLength) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

// Short month names ("Jan".."Dec") indexed by a 1-12 month number. Used by the
// significant-date pickers (month <select>) and by formatSignificantDate.
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Short month name for a 1-12 month number, e.g. monthName(1) === 'Jan'.
 * @param {number} month - 1-based month
 * @returns {string} three-letter month abbreviation
 */
export function monthName(month) {
    return SHORT_MONTHS[month - 1];
}

/**
 * Format a "significant date" object — `{ month, day, year? }`, NOT a Date or
 * Firestore Timestamp. Returns "Mon D, YYYY", or "Mon D" when year is absent.
 * @param {{month: number, day: number, year?: number}} sd
 * @returns {string}
 */
export function formatSignificantDate(sd) {
    const m = monthName(sd.month);
    return sd.year ? `${m} ${sd.day}, ${sd.year}` : `${m} ${sd.day}`;
}
